// web-frontend/src/components/chat/MessageBubble.jsx
/**
 * 메시지 버블 컴포넌트 (v3.0)
 * 
 * 🔥 v3.0 변경사항 (2025-01-23):
 * - [요구사항 5] SUGGEST_LANG_CHANGE 액션: 언어 변경 버튼 UI 추가
 * - [추가제안] related_companies: 비즈니스 미팅 예약 버튼 추가
 * - onAction prop 추가 (통합 액션 핸들러)
 * - 네이밍 컨벤션: API 응답(snake_case) 유지, 내부 함수(camelCase)
 * 
 * 🔥 v2.1 변경사항:
 * - 설문조사 바로가기 버튼 추가
 * - 관련 기업 카드 표시
 */
import React, { memo, useMemo } from 'react';
import { 
  Bot, 
  User, 
  AlertCircle, 
  Check, 
  ClipboardList, 
  Building2, 
  MapPin, 
  Tag,
  Globe,
  Calendar,
  ArrowRight,
  RefreshCw,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';

// 상수
import { CHATBOT_ACTIONS, SUPPORTED_LANGUAGES, getLanguageName } from '../../shared/constants/chatbotActions';
import i18nService from '../../shared/services/i18n';
import useAuthStore from '../../shared/stores/authStore';
import { isOwnCompany } from '../../shared/utils/companyUtils';

import logger from '../../shared/utils/logger';
import './MessageBubble.css';

/**
 * 간단한 마크다운 파서 (볼드체, 이탤릭체, 링크)
 * @param {string} text - 파싱할 텍스트
 * @returns {React.ReactNode[]} - 파싱된 React 노드 배열
 */
const renderMarkdown = (text) => {
  if (!text) return '';
  
  // 🔥 v3.1 - 거리 및 시간 정보 텍스트 강제 제거 (Regex 필터링)
  // "거리: 약 ...m" 또는 "예상 시간: 약 ...초" 패턴이 포함된 라인을 삭제합니다.
  const filteredText = text
    .split('\n')
    .filter(line => !line.includes('거리:') && !line.includes('예상 시간:'))
    .join('\n')
    .trim();

  // 볼드(**text**) 및 이탤릭(*text*) 처리
  const parts = filteredText.split(/(\*\*.+?\*\*|\*.+?\*)/g);
  
  return parts.map((part, index) => {
    // 볼드체: **텍스트**
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    // 이탤릭체: *텍스트*
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

/**
 * 시간 문자열 포맷팅
 * @param {string|Date} timestamp - 타임스탬프
 * @returns {string} - HH:MM 형식의 시간 문자열
 */
const formatTime = (timestamp) => {
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return '--:--';
    }
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    logger.warn('시간 포맷팅 실패:', error);
    return '--:--';
  }
};

/**
 * 언어 코드에 따른 버튼 텍스트 생성
 * @param {string} langCode - 언어 코드
 * @returns {string} - 버튼 텍스트
 */
const getLanguageButtonText = (langCode) => {
  return i18nService.t(`chatbot.lang_accept_${langCode}`) || `Change to ${langCode}`;
};

/**
 * 메시지 버블 컴포넌트
 * 
 * @param {Object} props
 * @param {Object} props.message - 메시지 객체
 * @param {string} props.message.id - 메시지 ID
 * @param {string} props.message.type - 메시지 타입 ('user' | 'bot')
 * @param {string} props.message.content - 메시지 내용
 * @param {string} props.message.timestamp - 타임스탬프
 * @param {boolean} [props.message.isTyping] - 타이핑 중 여부
 * @param {boolean} [props.message.isStreaming] - 스트리밍 중 여부
 * @param {boolean} [props.message.isError] - 에러 여부
 * @param {string} [props.message.action] - 액션 타입 (SURVEY_PROMPT, NAVIGATE, SUGGEST_LANG_CHANGE 등)
 * @param {Object} [props.message.action_payload] - 액션 페이로드 (snake_case - API 응답 형식)
 * @param {Object} [props.message.actionPayload] - 액션 페이로드 (camelCase - 레거시 호환)
 * @param {Array} [props.message.companies] - 관련 기업 목록 (레거시)
 * @param {Array} [props.message.related_companies] - 관련 기업 목록 (snake_case - API 응답 형식)
 * @param {boolean} props.isMyMessage - 내 메시지 여부
 * @param {Function} [props.onSurveyClick] - 설문조사 버튼 클릭 핸들러
 * @param {Function} [props.onCompanyClick] - 기업 클릭 핸들러
 * @param {Function} [props.onAction] - 통합 액션 핸들러 (action, payload) => void
 */
const MessageBubble = memo(({
  message,
  isMyMessage,
  onSurveyClick,
  onCompanyClick,
  onAction,  // 🔥 v3.0: 통합 액션 핸들러
  onRetry,   // 🤖 Phase 1B: 실패 메시지 재시도
  onFeedback // 🤖 Phase 1C: 메시지 피드백 (thumbs up/down)
}) => {
  const { user } = useAuthStore();

  // 🔥 v3.0: 페이로드 통합 (snake_case 우선, camelCase 폴백)
  const actionPayload = message.action_payload || message.actionPayload || {};
  
  // 🔥 v3.0: 관련 기업 목록 통합 (snake_case 우선)
  const relatedCompanies = message.related_companies || message.companies || [];
  const meetingCompanies = useMemo(
    () => relatedCompanies.filter((company) => !isOwnCompany(company, user)),
    [relatedCompanies, user],
  );
  
  // 🔥 시간 문자열 메모이제이션 (timestamp가 변경될 때만 재계산)
  const timeString = useMemo(() => {
    return formatTime(message.timestamp);
  }, [message.timestamp]);
  
  // 🔥 CSS 클래스 메모이제이션
  const containerClassName = useMemo(() => {
    return `message-container ${isMyMessage ? 'my-message' : 'bot-message'}`;
  }, [isMyMessage]);
  
  const bubbleClassName = useMemo(() => {
    const classes = ['message-bubble'];
    classes.push(isMyMessage ? 'me' : 'bot');
    if (message.isError) classes.push('error');
    if (message.isStreaming) classes.push('streaming');
    return classes.join(' ');
  }, [isMyMessage, message.isError, message.isStreaming]);
  
  // 🔥 메시지 내용 메모이제이션 (content가 변경될 때만 재파싱)
  const renderedContent = useMemo(() => {
    return renderMarkdown(message.content);
  }, [message.content]);
  
  // 🔥 v2.1: 설문조사 필요 여부
  const showSurveyButton = useMemo(() => {
    return !isMyMessage && 
           message.action === 'SURVEY_PROMPT' && 
           !message.isStreaming && 
           !message.isTyping;
  }, [isMyMessage, message.action, message.isStreaming, message.isTyping]);
  
  // 🔥 v3.0: 언어 변경 제안 여부
  const showLanguageChangeButtons = useMemo(() => {
    return !isMyMessage && 
           message.action === CHATBOT_ACTIONS.SUGGEST_LANG_CHANGE && 
           actionPayload?.target_lang &&
           !message.isStreaming && 
           !message.isTyping;
  }, [isMyMessage, message.action, actionPayload, message.isStreaming, message.isTyping]);
  
  // 🔥 v3.0: 관련 기업 미팅 예약 버튼 표시 여부
  const showMeetingButtons = useMemo(() => {
    return !isMyMessage && 
           meetingCompanies.length > 0 && 
           !message.isStreaming && 
           !message.isTyping &&
           // 설문 필요 액션이 아닐 때만 표시
           message.action !== CHATBOT_ACTIONS.SURVEY_REQUIRED &&
           message.action !== CHATBOT_ACTIONS.SURVEY_PROMPT;
  }, [isMyMessage, meetingCompanies.length, message.isStreaming, message.isTyping, message.action]);
  
  // 🔥 v3.0: 액션 핸들러 래퍼
  const handleAction = (action, payload) => {
    if (onAction) {
      onAction(action, payload);
    } else {
      logger.warn('onAction prop이 전달되지 않음. 액션:', action);
    }
  };
  
  // 🔥 v3.0: 언어 변경 핸들러
  const handleLanguageChange = (targetLang) => {
    handleAction(CHATBOT_ACTIONS.SET_APP_LANG, { language: targetLang });
  };
  
  // 🔥 v3.0: 미팅 예약 핸들러
  const handleMeetingRequest = (company) => {
    handleAction(CHATBOT_ACTIONS.OPEN_MEETING_MODAL, { 
      companyId: company.id,
      companyName: company.company_name 
    });
  };
  
  // 🔥 v3.0: 팝업 닫기 (언어 변경 거절)
  const handleDeclineLanguageChange = () => {
    handleAction(CHATBOT_ACTIONS.CLOSE_POPUP, {});
  };
  
  return (
    <div className={containerClassName}>
      {/* 봇 아바타 (왼쪽) */}
      {!isMyMessage && (
        <div className="message-avatar">
          <Bot size={20} />
        </div>
      )}
      
      <div className="message-content-wrapper">
        <div className={bubbleClassName}>
          
          {/* 🔥 타이핑 인디케이터 (점 3개 애니메이션) */}
          {message.isTyping ? (
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          ) : (
            <div className="message-text">
              {renderedContent}
              {/* 🔥 스트리밍 중 커서 표시 */}
              {message.isStreaming && (
                <span className="streaming-cursor">▋</span>
              )}
            </div>
          )}
          
          {/* 🔥 타이핑 중이 아닐 때만 시간 및 상태 표시 */}
          {!message.isTyping && (
            <div className="message-info">
              <span className="message-time">{timeString}</span>
              {isMyMessage && (
                <span className="message-status">
                  <Check size={12} />
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* ==================== 🔥 v3.0: 언어 변경 제안 버튼 ==================== */}
        {showLanguageChangeButtons && (
          <div className="action-buttons-container lang-change-buttons">
            <button
              className="action-btn accept-lang-btn"
              onClick={() => handleLanguageChange(actionPayload.target_lang)}
            >
              <Globe size={14} />
              <span>{getLanguageButtonText(actionPayload.target_lang)}</span>
            </button>
            <button
              className="action-btn decline-lang-btn"
              onClick={handleDeclineLanguageChange}
            >
              <span>{i18nService.t('chatbot.lang_decline') || '아니요, 그대로 쓸게요'}</span>
            </button>
          </div>
        )}
        
        {/* ==================== 🔥 v3.0: 비즈니스 미팅 예약 버튼 ==================== */}
        {showMeetingButtons && (
          <div className="company-quick-links">
            <div className="quick-links-header">
              <Calendar size={14} />
              <span>{i18nService.t('chatbot.meeting_header') || '비즈니스 상담 예약'}</span>
            </div>
            <div className="quick-links-list">
              {meetingCompanies.slice(0, 3).map((company) => (
                <button
                  key={company.id}
                  className="meeting-request-btn"
                  onClick={() => handleMeetingRequest(company)}
                >
                  <Building2 size={14} />
                  <span className="company-name">{company.company_name}</span>
                  <span className="meeting-label">{i18nService.t('chatbot.meeting_reserve') || '상담 예약'}</span>
                  <ArrowRight size={12} />
                </button>
              ))}
              {meetingCompanies.length > 3 && (
                <div className="more-companies-hint">
                  {(i18nService.t('chatbot.more_companies') || '외 {count}개 기업').replace('{count}', meetingCompanies.length - 3)}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 🔥 v2.1: 설문조사 바로가기 버튼 */}
        {showSurveyButton && (
          <button
            className="survey-prompt-button"
            onClick={onSurveyClick}
          >
            <ClipboardList size={18} />
            <span>{i18nService.t('chatbot.survey_shortcut') || '설문조사 바로가기'}</span>
          </button>
        )}
        
        {/* 에러 라벨 + 재시도 버튼 */}
        {message.isError && (
          <div className="error-row">
            <span className="error-label">
              <AlertCircle size={12} /> {i18nService.t('chatbot.send_failed') || '전송 실패'}
            </span>
            {onRetry && (
              <button className="retry-btn" onClick={() => onRetry(message.content)}>
                <RefreshCw size={12} /> 재시도
              </button>
            )}
          </div>
        )}

        {/* 봇 메시지 피드백 (스트리밍 완료 후에만) */}
        {!isMyMessage && !message.isTyping && !message.isStreaming && !message.isError && message.id !== 'welcome' && onFeedback && (
          <div className="feedback-row">
            <button
              className={`feedback-btn ${message.feedbackGiven === 'up' ? 'active-up' : ''}`}
              onClick={() => onFeedback(message.id, true)}
              disabled={!!message.feedbackGiven}
            >
              <ThumbsUp size={12} />
            </button>
            <button
              className={`feedback-btn ${message.feedbackGiven === 'down' ? 'active-down' : ''}`}
              onClick={() => onFeedback(message.id, false)}
              disabled={!!message.feedbackGiven}
            >
              <ThumbsDown size={12} />
            </button>
          </div>
        )}
      </div>
      
      {/* 사용자 아바타 (오른쪽) */}
      {isMyMessage && (
        <div className="message-avatar user">
          <User size={20} />
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // 🔥 커스텀 비교 함수: 실제로 변경된 경우에만 리렌더링
  
  const prevMsg = prevProps.message;
  const nextMsg = nextProps.message;
  
  // ID가 다르면 리렌더링
  if (prevMsg.id !== nextMsg.id) return false;
  
  // 내 메시지 여부가 다르면 리렌더링
  if (prevProps.isMyMessage !== nextProps.isMyMessage) return false;
  
  // 타이핑 상태가 다르면 리렌더링
  if (prevMsg.isTyping !== nextMsg.isTyping) return false;
  
  // 스트리밍 상태가 다르면 리렌더링
  if (prevMsg.isStreaming !== nextMsg.isStreaming) return false;
  
  // 에러 상태가 다르면 리렌더링
  if (prevMsg.isError !== nextMsg.isError) return false;
  
  // 내용이 다르면 리렌더링 (스트리밍 중 chunk 추가 시)
  if (prevMsg.content !== nextMsg.content) return false;
  
  // 🔥 v2.1: 액션이 다르면 리렌더링
  if (prevMsg.action !== nextMsg.action) return false;
  
  // 🔥 v3.0: 액션 페이로드 변경 체크
  const prevPayload = prevMsg.action_payload || prevMsg.actionPayload;
  const nextPayload = nextMsg.action_payload || nextMsg.actionPayload;
  if (JSON.stringify(prevPayload) !== JSON.stringify(nextPayload)) return false;
  
  // 🔥 v3.0: 관련 기업 목록 변경 체크 (snake_case 포함)
  const prevCompanies = prevMsg.related_companies || prevMsg.companies;
  const nextCompanies = nextMsg.related_companies || nextMsg.companies;
  if (prevCompanies?.length !== nextCompanies?.length) return false;
  
  // 🔥 v3.0: onAction 변경 체크
  if (prevProps.onAction !== nextProps.onAction) return false;
  
  // 동일하면 리렌더링 스킵
  return true;
});

// 디버깅용 displayName 설정
MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
